import _ from 'underscore'

import PATHS from 'src/router/paths'
import Router from 'src/router/index'

import api from 'src/store/api'
import COLUMNS from 'src/utils/columns'
import { fireanalytics } from 'src/store/firebase'
import http from 'src/store/http'
import dates from 'src/utils/dates'
import FILTER from 'src/utils/filter'
import { groupBy, makeGroup } from 'src/utils/group'
import merge from 'src/utils/merge'
import { copyObject } from 'src/utils/objects'
import sort from 'src/utils/sort'
import transform from 'src/utils/transform'

import search from './utils/search'
import navigation from './utils/navigation'

import actionNames from './actionNames'
import stub from '@/store/stub'

const { direction: SortDirection } = sort

const makeApiParamsForTeamEndpoints = (query) => ({
  publisherId: query.publisherId,
  teamId: query.teamId,
})
const makeApiParamsForPublisherEndpoints = (query) => ({
  dateRange: query.dateRange,
  publisherId: query.publisherId,
})

const dateProperties = ['date', 'dateRange', 'endDate', 'startDate', 'timeEntryDate']

const isCustomDate = (value) => /yesterday|today|tomorrow/.test(value) || /last-(week|month|quarter|year)/.test(value) || /this-(week|month|quarter|year)/.test(value) || /(week|month|quarter|year)-to-date/.test(value)
const isPrettyDate = (value) => /\d\sminute|\d\shour|\d\sday|\d\sweek|\d\smonth|\d\squarter|\d\syear/i.test(value) || isCustomDate(value)

const getRangeWithEqualRelativeDates = (givenDate, operator) => operator === 'eq' && [givenDate, givenDate]
const getRangeWithNotEqualToRelativeDate = (givenDate, operator) => operator === 'neq' && [dates.get('1970-01-01'), dates.get('2485-12-31')]
const getRangeWithEndDateGreaterThanOrEqualToDate = (givenDate, operator) => operator === 'gte' && [givenDate, dates.get('2485-12-31')]
const getRangeWithEndDateLessThanOrEqualToDate = (givenDate, operator) => operator === 'lte' && [dates.get('1970-01-01'), givenDate]
const getRangeWithEndDateRelativeToToday = (trimmedValue, realValue, operator) => {
  const prettyRangeMap = {
    'last-week': dates.getLastWeek,
    'last-month': dates.getLastMonth,
    'last-year': dates.getLastYear,
    'this-week': dates.getThisWeek,
    'this-month': dates.getThisMonth,
    'this-year': dates.getThisYear,
    'week-to-date': dates.wtd,
    'month-to-date': dates.mtd,
    'year-to-date': dates.ytd,
  }
  const getMappedDateRange = () => prettyRangeMap[trimmedValue] && prettyRangeMap[trimmedValue]()
  return operator === 'range' && (getMappedDateRange() || realValue)
}

const getDateFromPrettyString = (val) => {
  const trimmedValue = typeof val === 'string' && val.trim()
  return !!trimmedValue && isPrettyDate(val) && dates.getRelativeDate(trimmedValue)
}

const getDateFromString = (value) => {
  const trimmedValue = typeof value === 'string' && value.trim()
  return getDateFromPrettyString(trimmedValue) || dates.get(trimmedValue)
}

const getDateRangeFromString = (value, operator) => {
  const isDateRangeString = typeof value === 'string' && /,/.test(value)
  const realValue = isDateRangeString && value.split(',')
  const dateRangeArray = realValue && realValue.map((val) => getDateFromString(val, operator))

  if (dateRangeArray && dateRangeArray.length < 2) {
    const givenDate = dateRangeArray[0]
    return getRangeWithEqualRelativeDates(givenDate, operator)
      || getRangeWithEndDateGreaterThanOrEqualToDate(givenDate, operator)
      || getRangeWithEndDateLessThanOrEqualToDate(givenDate, operator)
      || getRangeWithEndDateRelativeToToday(givenDate, realValue[0], operator)
      || getRangeWithNotEqualToRelativeDate(givenDate, operator)
  }

  return dateRangeArray
}

const getRelativeDateRangeFromPrettyDate = (val, operator) => {
  const trimmedValue = typeof val === 'string' && val.trim()
  const getDateRange = () => {
    const realValue = getDateFromPrettyString(trimmedValue, operator)
    const givenDate = realValue && dates.get(realValue)

    return getRangeWithEqualRelativeDates(givenDate, operator)
      || getRangeWithEndDateGreaterThanOrEqualToDate(givenDate, operator)
      || getRangeWithEndDateLessThanOrEqualToDate(givenDate, operator)
      || getRangeWithEndDateRelativeToToday(trimmedValue, realValue, operator)
      || getRangeWithNotEqualToRelativeDate(givenDate, operator)
  }

  return trimmedValue && getDateRange()
}

const getDateRangeFromArray = (value, operator) => {
  const isArray = Array.isArray(value)

  return isArray && value.map((val) => {
    const trimmedValue = typeof val === 'string' && val.trim()
    const realValue = getDateFromPrettyString(trimmedValue, operator)
    const relativeDate = realValue && dates.get(realValue)
    return relativeDate || dates.get(trimmedValue)
  })
}

const getDateRangeAsObjectArray = (value, operator) => {
  const getDateRange = () => getDateRangeFromString(value, operator) || getDateRangeFromArray(value, operator) || getRelativeDateRangeFromPrettyDate(value, operator)
  return value && getDateRange()
}

const filterDateRangeParameters = (reportParameters) => {
  const getDateRangeFilters = () => reportParameters.filter((param) => param.action === 'filterBy' && dateProperties.indexOf(param.propertyName) !== -1)
  return !!reportParameters && getDateRangeFilters()
}

const getDefaultDateRangeForApi = () => {
  const startDate = '1970-01-01'
  const endDate = dates.get('2485-12-31').format('YYYY-MM-DD')

  return { endDate, startDate }
}

const flattenDateParametersForApi = (dateRangeParameters) => {
  const isRangeOperator = (operator) => operator === 'range'
  const isNotEqualOperator = (operator) => operator === 'neq'
  const isEqualOperator = (operator) => operator === 'eq'
  const isGteOperator = (operator) => operator === 'gte'
  const isLteOperator = (operator) => operator === 'lte'

  const getAllDates = (operator) => {
    const startDate = '1970-01-01'
    const endDate = dates.get('2485-12-31').format('YYYY-MM-DD')
    const newValue = [startDate, endDate]

    return { operator, value: newValue }
  }

  return dateRangeParameters.reduce((newDateParameter, parameter, parameterIndex) => {
    const { operator, value } = parameter

    if (parameterIndex === 0) {
      // Return date range if we already have it.
      if (isRangeOperator(operator)) {
        const [startDateObject, endDateObject] = getDateRangeAsObjectArray(value, operator)
        const newValue = [startDateObject.format('YYYY-MM-DD'), endDateObject.format('YYYY-MM-DD')]
        return { operator, value: newValue }
      }

      // Else, make date range of other parameters.
      const getEqualDateRange = () => {
        const getDate = () => {
          const dateString = getDateFromString(value)
          const newValue = [dateString, dateString]
          return { operator: 'range', value: newValue }
        }
        return operator === 'eq' && getDate()
      }
      const getNotEqualDateRange = () => operator === 'neq' && getAllDates(operator)
      const getLteDateRange = () => {
        const getDate = () => {
          const endDateString = getDateFromString(value)
          const newValue = ['1970-01-01', endDateString]
          return { operator: 'range', value: newValue }
        }
        return operator === 'lte' && getDate()
      }
      const getGteDateRange = () => {
        const getDate = () => {
          const startDateString = getDateFromString(value)
          const newValue = [startDateString, dates.get('2485-12-31').format('YYYY-MM-DD')]
          return { operator: 'range', value: newValue }
        }
        return operator === 'gte' && getDate()
      }

      return getEqualDateRange() || getNotEqualDateRange() || getLteDateRange() || getGteDateRange() || getAllDates(operator)
    }

    // If the other operator is neq, then we can return the range, ignoring the neq. The filter will handle the rest.
    // If the other operator is a range, then we need to get the outer bounds of both ranges.
    // If the other operator is a gte, then we need to get the outer bounds of the range. If gte is less than the range's start date, then we need to use the gte's value as the start date.
    // If the other operator is a lte, then we need to get the outer bounds of the range. If lte is greater than the range's end date, then we need to use the lte's value as the end date.
    // If the other operator is an eq, then we need to get the outer bounds of the range. If eq is less than the range's start date, then we need to use the eq's value as the start date. If eq is greater than the range's end date, then we need to use the eq's value as the end date.
    const handleNotEqualOperator = (otherParameter) => isNotEqualOperator(otherParameter) && { operator, value }
    const handleRangeOperator = (otherParameter) => {
      const handleThis = () => {
        // Get outer bounds of both ranges.
        const [startDateObject, endDateObject] = getDateRangeAsObjectArray(value, operator)
        const [otherStartDateObject, otherEndDateObject] = getDateRangeAsObjectArray(otherParameter.value, otherParameter.operator)
        const startDate = startDateObject.isBefore(otherStartDateObject) ? startDateObject.format('YYYY-MM-DD') : otherStartDateObject.format('YYYY-MM-DD')
        const endDate = endDateObject.isAfter(otherEndDateObject) ? endDateObject.format('YYYY-MM-DD') : otherEndDateObject.format('YYYY-MM-DD')
        return { operator, value: [startDate, endDate] }
      }
      return isRangeOperator(otherParameter) && handleThis()
    }
    const handleGteOperator = (otherParameter) => {
      const handleThis = () => {
        // Get outer bounds of range. If gte is less than the range's start date, then we need to use the gte's value as the start date.
        const [startDateObject, endDateObject] = getDateRangeAsObjectArray(value, operator)
        const otherStartDateObject = getDateFromString(otherParameter.value)
        const startDate = startDateObject.isBefore(otherStartDateObject) ? startDateObject.format('YYYY-MM-DD') : otherStartDateObject.format('YYYY-MM-DD')
        return { operator, value: [startDate, endDateObject.format('YYYY-MM-DD')] }
      }
      return isGteOperator(otherParameter) && handleThis()
    }
    const handleLteOperator = (otherParameter) => {
      const handleThis = () => {
        // Get outer bounds of range. If lte is greater than the range's end date, then we need to use the lte's value as the end date.
        const [startDateObject, endDateObject] = getDateRangeAsObjectArray(value, operator)
        const otherEndDateObject = getDateFromString(otherParameter.value)
        const endDate = endDateObject.isAfter(otherEndDateObject) ? endDateObject.format('YYYY-MM-DD') : otherEndDateObject.format('YYYY-MM-DD')
        return { operator, value: [startDateObject.format('YYYY-MM-DD'), endDate] }
      }
      return isLteOperator(otherParameter) && handleThis()
    }
    const handleEqualOperator = (otherParameter) => {
      const handleThis = () => {
        // Get outer bounds of range. If eq is less than the range's start date, then we need to use the eq's value as the start date. If eq is greater than the range's end date, then we need to use the eq's value as the end date.
        const [startDateObject, endDateObject] = getDateRangeAsObjectArray(value, operator)
        const otherDateObject = getDateFromString(otherParameter.value)
        const startDate = startDateObject.isBefore(otherDateObject) ? startDateObject.format('YYYY-MM-DD') : otherDateObject.format('YYYY-MM-DD')
        const endDate = endDateObject.isAfter(otherDateObject) ? endDateObject.format('YYYY-MM-DD') : otherDateObject.format('YYYY-MM-DD')
        return { operator, value: [startDate, endDate] }
      }
      return isEqualOperator(otherParameter) && handleThis()
    }

    if (isRangeOperator(operator)) {
      return handleRangeOperator(parameter)
    }
    if (isNotEqualOperator(operator)) {
      // If conjunction is OR, use the neq range.
      return handleNotEqualOperator(parameter)
    }
    if (isEqualOperator(operator)) {
      return handleEqualOperator(parameter)
    }
    if (isGteOperator(operator)) {
      return handleGteOperator(parameter)
    }
    if (isLteOperator(operator)) {
      return handleLteOperator(parameter)
    }

    return newDateParameter
  }, { operator: null, value: null })
}

const makeDateRangeForApi = (reportParameters) => {
  const dateRangeParameters = filterDateRangeParameters(reportParameters)
  const getMergedDateParameter = (dateRangeParams) => {
    const { value } = flattenDateParametersForApi(dateRangeParams)
    return {
      endDate: value[1],
      startDate: value[0],
    }
  }
  return dateRangeParameters && dateRangeParameters.length ? getMergedDateParameter(dateRangeParameters) : getDefaultDateRangeForApi()
}

const fetchMap = {
  discrepancy: (dispatch, params, query) => api.getPublisherDiscrepancy(makeApiParamsForTeamEndpoints(query)),
  pacing: (dispatch, params, query) => api.getLineItemsForPublisher(makeApiParamsForTeamEndpoints(query)),

  creatives: (dispatch, params) => api.getGamLicas(params),
  lineitems: (dispatch, params) => api.getGamLineItems(params),
  orders: (dispatch, params) => api.getGamOrders(params),

  'adx-health': (dispatch, params, query) => api.getHealthAdx(makeApiParamsForPublisherEndpoints(query)),
  'network-health': (dispatch, params, query) => api.getHealthNetwork(makeApiParamsForPublisherEndpoints(query)),
  'adx-and-open-bidding': (dispatch, params, query) => api.getYieldPartner(makeApiParamsForPublisherEndpoints(query)),

  organization: (dispatch, params, query, user) => dispatch('auth/fetchClientsAndPublishers', user, { root: true }),
  project: () => api.getProjects(),
  service: () => api.getServices(),

  person: (dispatch, params, query, user) => dispatch('auth/fetchPersonsAndUsers', { params, query, user }, { root: true }),
  'internal-team': (dispatch, params, query) => (query.teamId ? api.getInternalTeamById(query.teamId) : api.getInternalTeams()),

  'client-assignments': (dispatch, params, query) => api.getClientAssignments(query),
  'time-entries': (dispatch, params, query) => {
    const [startDateObject, endDateObject] = dates.mtd()
    const endDate = endDateObject.format('YYYY-MM-DD')
    const startDate = startDateObject.format('YYYY-MM-DD')

    query.endDate = endDate
    query.startDate = startDate

    return dispatch('fetchTimeEntries', { endDate, startDate })
  },

  [PATHS.INTERNAL.TIME_ROLLUP]: async (dispatch, params, query, user) => {
    const currentSelectedReport = await dispatch('getCurrentSelectedReport')
    const { parameters } = currentSelectedReport || {}
    const apiQuery = makeDateRangeForApi(parameters)

    return dispatch('fetchTimeRollup', { apiQuery, user })
  },
  [PATHS.INTERNAL.TIME_SHEET]: (dispatch, params, query, user) => {
    const todayObject = dates.now()
    const endDate = todayObject.add(1, 'month').format('YYYY-MM-DD')
    const startDate = todayObject.subtract(1, 'month').format('YYYY-MM-DD')
    const apiQuery = {
      personId: user.personId,
      endDate,
      startDate,
    }

    return dispatch('fetchTime', { apiQuery, user })
  },
  timeType: () => api.getTimeTypes(),
}

const stubMap = {
  pacing: stub.reports_pacing_line_items_summary.response,
  discrepancy: stub.reports_discrepancy_line_items.response,
}

const actions = {
  async setup({ commit }, data) {
    const { columns, params, query } = data
    // Handles legacy routes by checking params for associationId, reportPath, and subId. Nevertheless, we prioritize query for these values.
    const { associationId, baseReportName, reportPath, routeName, subId } = params
    const { associationId: queryAssociationId, metricPath: queryMetricPath, reportPath: queryReportPath, subId: querySubId } = query || {}

    if (columns) commit('columns', columns)

    if (query) commit('query', query)

    commit('metricPath', queryMetricPath)
    commit('reportPath', queryReportPath || reportPath)
    commit('associationId', queryAssociationId || associationId)
    commit('subId', querySubId || subId)

    commit('routeName', routeName)
    commit('baseReportName', baseReportName)

    return data
  },

  async transform({ dispatch, state }, { endpointKey, data, query }) {
    const getColumnsByKey = () => {
      const storedColumns = state.columns[endpointKey] || state.columnDefaults[endpointKey]
      return storedColumns.reduce((columns, column) => {
        columns[column.key] = column
        return columns
      }, {})
    }
    const transformMap = {
      discrepancy: (response) => transform.discrepancy(query.networkCode, getColumnsByKey(), response.content),
      pacing: (response) => transform.pacing(query.networkCode, getColumnsByKey(), response.content),

      [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: (response) => transform[PATHS.PROGRAMMATIC.NETWORK_HEALTH](response.content, getColumnsByKey()),
      [PATHS.PROGRAMMATIC.ADX_HEALTH]: (response) => transform[PATHS.PROGRAMMATIC.ADX_HEALTH](response.content, getColumnsByKey()),
      [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: (response) => transform[PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING](response.content, getColumnsByKey()),

      creatives: () => transform.freezeAll.bind(transform, query.networkCode, getColumnsByKey()),
      lineitems: () => transform.freezeAll.bind(transform, query.networkCode, getColumnsByKey()),
      orders: () => transform.freezeAll.bind(transform, query.networkCode, getColumnsByKey()),

      organization: (responses) => dispatch('auth/transformClientsAndPublishers', responses, { root: true }),
      project: (response) => dispatch('transformProjectItems', response),
      service: (response) => dispatch('transformServiceItems', response),

      person: async (responses) => dispatch('auth/transformPersonUsers', responses, { root: true }),
      'internal-team': (response) => dispatch('transformInternalTeamItems', response),

      [PATHS.INTERNAL.TIME_ROLLUP]: (responses) => dispatch('transformTimeRollup', responses),
      [PATHS.INTERNAL.TIME_SHEET]: (responses) => dispatch('transformTime', responses),

      'client-assignments': (response) => dispatch('transformClientAssignments', response),
      'time-entries': (response) => dispatch('transformTimeItems', response.content),
      timeType: (response) => dispatch('transformTimeTypeItems', response),
    }

    return transformMap[endpointKey](data)
  },

  /*
   * Fetches request from the API, passing the parameters along.
   * @param {Object} context
   * @param {function} context.dispatch
   * @param {Object} context.rootState
   * @param {Object} data
   * @param {Object} data.params { baseReportName, routeName }
   * @param {Object} data.query { associationId, dateRange, subId }
   * @param {Object} [data.user]
   */
  request({ dispatch, rootState }, { endpointKey, params, query, user: givenUser }) {
    const user = givenUser || rootState.auth.user

    const isDemoRoute = () => {
      const demoRoutes = ['endpointKey', 'dashboard', 'discrepancy', 'pacing', 'adx-health', 'network-health', 'adx-and-open-bidding']
      return demoRoutes.indexOf(endpointKey) !== -1
    }
    const isDemoPublisher = query && query.associationId === 'DEMO2021'

    const getError = () => ({ content: [], error: { message: 'Endpoint Not Found', status: 404 } })

    const getStub = async () => {
      const stubResponse = typeof stubMap[endpointKey] === 'function' && await stubMap[endpointKey]()
      const normalResponse = stubResponse && {
        content: stubResponse.content,
        totalCount: stubResponse.totalCount,
      }

      if (stubResponse && Array.isArray(stubResponse.content)) {
        normalResponse.content = stubResponse.content.reduce((content, item) => {
          const newItem = copyObject(item)
          content.push(newItem)
          return content
        }, [])
      }

      return normalResponse || getError()
    }
    const getApi = () => (typeof fetchMap[endpointKey] === 'function' && fetchMap[endpointKey](dispatch, params, query, user)) || getError()

    return isDemoPublisher && isDemoRoute() ? getStub() : getApi()
  },

  /**
   * Updates the store, incrementing the viewId, which updates the view.
   */
  async updateView({ commit, dispatch }, { endpointKey, items }) {
    const root = { root: true }
    const message = endpointKey !== PATHS.INTERNAL.TIME_SHEET ? 'Rendered ' : 'Stored '

    commit('viewId', endpointKey)
    dispatch('messages/hideLoadingMessage', { componentName: endpointKey, text: `${message} ${endpointKey}` }, root)

    return items
  },
  /**
   * Updates the store, then increments the viewId, which updates the view.
   */
  async store({ commit }, { endpointKey, items, query }) {
    commit('items', { endpointKey, items, query })
    commit('viewId', endpointKey)

    return items
  },

  normalizeReportParameters(context, parameters) {
    const findDateParameter = (parameter) => {
      const isFilterParameter = () => parameter.action === 'filterBy'
      const isDateParameter = () => COLUMNS.isTypeDate(parameter.propertyName)
      return isFilterParameter() && isDateParameter()
    }

    return parameters.reduce((filterDataAsObject, parameter) => {
      const foundDateFilterParameter = parameter.action === 'filterBy' && findDateParameter(parameter)
      const newParameter = copyObject(parameter)

      if (foundDateFilterParameter) {
        const { operator, value: foundValue } = parameter
        const getDateRange = () => {
          const dateRange = operator === 'range' && getDateRangeAsObjectArray(foundValue, operator)
          return dateRange && dateRange.map((date) => date.format('YYYY-MM-DD'))
        }
        const getDate = () => {
          const isDateString = typeof foundValue === 'string'
          return isDateString && getDateFromString(foundValue, operator)
        }

        newParameter.value = getDateRange() || getDate() || foundValue
      }

      filterDataAsObject.push(newParameter)

      return filterDataAsObject
    }, [])
  },

  /**
   * Creates a function to filter and gather metadata about items in a group.
   * @param context
   * @param data
   * @param data.filterData
   * @param data.baseReportName
   * @returns {function(Object, Object): Object}
   */
  reduceItemsInGroup(context, { endpointKey, filterData }) {
    return (resultsOfFilteringGroups, unfilteredGroup) => {
      const { items } = unfilteredGroup
      const getUnfilteredGroup = () => (!items?.length || !endpointKey) && unfilteredGroup
      const getFilteredGroup = () => {
        const filterKeys = Object.keys(filterData)
        const reportFilter = FILTER.report.bind(FILTER)
        const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)
        const filteredItems = items.reduce((filteredSet, item) => {
          if (isItemIncluded(item)) {
            filteredSet.push(item)
            resultsOfFilteringGroups.itemized.push(item)
          } else {
            resultsOfFilteringGroups.excluded.push(item)
          }
          return filteredSet
        }, [])

        return { ...unfilteredGroup, items: filteredItems, totalCount: filteredItems.length }
      }

      resultsOfFilteringGroups.included.push(getUnfilteredGroup() || getFilteredGroup())

      return resultsOfFilteringGroups
    }
  },
  /**
   * Loops through the groups to filter the items in each group.
   * @param {Object} context
   * @param {Object} groupedReport
   * @returns {Promise<Object[]>} { excluded: [], itemized: [], included: [] }
   */
  async filterGroupedItems({ dispatch }, { endpointKey, filterData, groups }) {
    const mapFilteredItemsToGroup = await dispatch('reduceItemsInGroup', { endpointKey, filterData })

    const resultsOfFilteringGroups = { excluded: [], included: [], itemized: [] }

    return groups.reduce(mapFilteredItemsToGroup, resultsOfFilteringGroups)
  },
  async filterGroupedItemsByAllMetrics({ dispatch }, { groups, reportOptions }) {
    return reportOptions.reduce(async (promiseItemsByMetric, reportOption) => {
      const itemsByMetric = await promiseItemsByMetric
      const { isMetric } = reportOption

      if (isMetric) {
        const parameters = reportOption.parameters.filter((param) => param.action !== 'sortBy')
        itemsByMetric[reportOption.reportPath] = await dispatch('applyFilterData', { groups, parameters })
      }

      return itemsByMetric
    }, Promise.resolve({}))
  },
  async calculateMetrics({ commit, dispatch, getters }, { endpointKey, groups, query }) {
    const { reportOptionsForSelectedTeam } = getters
    const metrics = await dispatch('filterGroupedItemsByAllMetrics', { endpointKey, groups, reportOptions: reportOptionsForSelectedTeam })

    commit('metrics', { endpointKey, metrics, query })

    commit('viewId', endpointKey)
  },

  async filterItems(context, { items, parameter }) {
    const reportFilter = FILTER.report.bind(FILTER)
    const { conditions, propertyName, operator, value } = parameter
    const hasConditions = Array.isArray(conditions) && conditions.length > 0

    if (hasConditions) {
      const results = conditions.reduce((filteredData, condition, conditionIndex) => {
        const { conjunction, propertyName: cPropertyName, operator: cOperator, value: cValue } = condition
        const hasOrConjunction = conjunction === 'OR'
        const filterData = { [cPropertyName]: [{ operator: cOperator, value: cValue }] }
        const filterKeys = [cPropertyName]
        const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)
        const isInitialFilter = conditionIndex === 0

        if (hasOrConjunction) {
          const dataSource = isInitialFilter ? items : filteredData.excluded

          dataSource.forEach((item) => {
            if (isItemIncluded(item)) {
              filteredData.included.push(item)
            } else if (isInitialFilter) {
              filteredData.excluded.push(item)
            }
          })
        } else {
          const dataSource = isInitialFilter ? items : filteredData.included
          const includedItems = []

          dataSource.forEach((item) => {
            if (isItemIncluded(item)) {
              includedItems.push(item)
            } else if (isInitialFilter) {
              filteredData.excluded.push(item)
            }
          })

          filteredData.included = includedItems
        }

        return filteredData
      }, { excluded: [], included: [] })

      return results
    }

    const filterData = { [propertyName]: [{ operator, value }] }
    const filterKeys = [propertyName]
    const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)

    return items.reduce((filteredData, item) => {
      if (isItemIncluded(item)) {
        filteredData.included.push(item)
      } else {
        filteredData.excluded.push(item)
      }

      return filteredData
    }, { excluded: [], included: [] })
  },
  async mergeItems({ getters }, { items, baseReportName, mergeMap, mergeByProperties }) {
    const { columnsByKey } = getters
    const properties = mergeByProperties[baseReportName] || []
    const mergeProperties = mergeMap[baseReportName]

    return !mergeProperties ? items : merge.set(items, properties, mergeProperties, columnsByKey)
  },
  async groupItems(context, { groupByProperties, items }) {
    return groupBy(items, groupByProperties)
  },
  sortItems(context, { columnKey, columnDirection, items }) {
    const valueGetter = (item) => (item.normalized ? item.normalized[columnKey] : item[columnKey])
    const sortDsc = columnDirection === SortDirection.ASC
    const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive

    return sorter(items, valueGetter)
  },

  applyFilterBy({ dispatch }, { groups, parameter }) {
    const isOrConjunction = parameter.conjunction === 'OR'

    return groups.reduce(async (groupedResults, originalGroup) => {
      const { excluded = [], groupName, included = [], isPartOfGroup, items, labels } = originalGroup
      const dataSource = isOrConjunction ? excluded || [] : items
      const filteredItems = await dispatch('filterItems', { items: dataSource, parameter })

      const newGroup = {
        groupName,
        isPartOfGroup,
        labels,
      }

      if (isOrConjunction) {
        included.push(...filteredItems.included)

        newGroup.excluded = filteredItems.excluded
        newGroup.included = included
        newGroup.items = included
      } else {
        excluded.push(...filteredItems.excluded)

        newGroup.excluded = excluded
        newGroup.included = filteredItems.included
        newGroup.items = filteredItems.included
      }

      newGroup.totalCount = newGroup.items.length

      groupedResults.push(newGroup)

      return groupedResults
    }, [])
  },
  applyGroupBy({ dispatch }, { groups, parameter }) {
    const groupByProperties = parameter.propertyName
    return groups.reduce(async (groupedResults, originalGroup) => {
      const { items } = originalGroup
      originalGroup.items = await dispatch('groupItems', { groupByProperties, items })
      groupedResults.push(originalGroup)
      return groupedResults
    }, [])
  },
  applyMergeBy({ dispatch }, { groups, parameter }) {
    const mergeByProperties = parameter.propertyName
    const baseReportMergeMap = []
    return groups.reduce(async (groupedResults, originalGroup) => {
      const { items } = originalGroup
      originalGroup.items = await dispatch('mergeItems', { baseReportMergeMap, items, mergeByProperties })
      groupedResults.push(originalGroup)
      return groupedResults
    }, [])
  },
  applySortBy({ dispatch }, { groups, parameter }) {
    const columnKey = parameter.propertyName
    const columnDirection = parameter.value

    return groups.reduce(async (groupedResults, originalGroup) => {
      const { items } = originalGroup
      originalGroup.items = await dispatch('sortItems', { columnKey, columnDirection, items })
      groupedResults.push(originalGroup)
      return groupedResults
    }, [])
  },
  async applyFilterData({ dispatch }, { items, groups, parameters }) {
    const actionMap = {
      filterBy: 'applyFilterBy',
      groupBy: 'applyGroupBy',
      mergeBy: 'applyMergeBy',
      sortBy: 'applySortBy',
    }
    const initialData = groups || [
      makeGroup({ items }),
    ]

    const filterData = await dispatch('normalizeReportParameters', parameters)

    return Object.keys(filterData).reduce(async (newDataPromise, parameterKey) => {
      const parameter = filterData[parameterKey]
      const newData = await newDataPromise
      const { action } = parameter
      const currentAction = actionMap[action]

      if (currentAction) {
        return dispatch(currentAction, { groups: newData, parameter })
      }

      return newData
    }, initialData)
  },
  /**
   * Applies report parameters to the data set.
   * @param {Object} context
   * @param {Function} context.dispatch
   * @param {Object} args
   * @param {Array} args.data
   * @param {Array} args.parameters
   * @returns {Promise} Promise represents the processed data array.
   */
  async applyReportParameters({ commit, dispatch, getters }, { items, parameters }) {
    const endpointKey = getters.currentPageName
    const sortByParameters = parameters.filter((param) => param.action === 'sortBy')
    const sortByDirections = sortByParameters ? sortByParameters.map((param) => param.value) : []
    const sortByKeys = sortByParameters ? sortByParameters.map((param) => param.propertyName) : []
    const sorting = sortByParameters ? sortByParameters.map((param) => ({ direction: param.value, key: param.propertyName })) : []

    commit('sorting', { endpointKey, sorting })
    commit('sortingByDirections', sortByDirections)
    commit('sortingByKeys', sortByKeys)

    return dispatch('applyFilterData', { items, parameters })
  },
  /**
   * Update the store with the new query data, then prompt the view to update.
   * @param {Object} context
   * @param data
   */
  async applyReportCriteria({ dispatch, getters }, { items: unfilteredItems, params, query }) {
    const { baseReportName, routeName } = params
    const { associationId, reportPath, subId } = query
    const filterByBillingCode = (items) => {
      const { isAssociationIdBillingCode } = getters
      return isAssociationIdBillingCode ? items.filter((item) => item.billingCode === associationId) : items
    }
    const filterByBillingCodes = (items) => {
      const { isAssociationIdBillingCode } = getters
      return isAssociationIdBillingCode ? items.filter((item) => {
        const hasBillingCodes = item.billingCodes && item.billingCodes.length
        return (hasBillingCodes && item.billingCodes.indexOf(associationId) !== -1) || item.billingCode === associationId
      }) : items
    }

    const filterMap = {
      organization: (items) => filterByBillingCode(items),
      [PATHS.INTERNAL.TIME_ROLLUP]: (items) => filterByBillingCode(items),
      person: (items) => filterByBillingCodes(items),
      project: (items) => filterByBillingCode(items),
    }
    const reportKey = baseReportName || routeName
    const prefilterData = routeName === 'internal' && associationId !== 'all-associations' && filterMap[reportKey]
    const items = typeof prefilterData === 'function' ? prefilterData(unfilteredItems) : unfilteredItems

    const reportOptions = getters.reportOptionsByReportCode[`${associationId}/${subId}`]
    const report = reportOptions && reportOptions.find((option) => option.baseReportName === baseReportName && option.routeName === routeName && option.reportPath === reportPath)
    const { parameters = [] } = report || {}

    return dispatch('applyReportParameters', { items, parameters })
  },

  /**
   * Transforms, then stores, the results from the request method.
   * @return items {Array} Row groups
   */
  async render({ commit, dispatch, getters, state }, { endpointKey, items, params, query }) {
    const groups = await dispatch('applyReportCriteria', { items, params, query })
    const groupItems = groups.reduce((allItems, group) => {
      allItems.push(...group.items)
      return allItems
    })
    const isTimeSheetRoute = endpointKey === PATHS.INTERNAL.TIME_SHEET

    commit('items', { endpointKey, items, query: { ...query, reportPath: 'all' } })
    commit('items', { endpointKey, items: groupItems, query })
    commit('results', { endpointKey, groups, query })

    commit('viewId', endpointKey)

    if (isTimeSheetRoute) {
      const { associationId, baseReportName, subId, summaries } = state
      const isAlert = false

      dispatch('tools/summaries', { associationId, baseReportName, isAlert, items: groupItems.items, subId, summaries }, { root: true })
      commit('viewId', endpointKey)
    } else {
      dispatch('calculateMetrics', { endpointKey, groups, query })
    }

    const { isDashboardRoute, isDeliveryRoute, isProgrammaticRoute } = getters

    if (isDashboardRoute || isDeliveryRoute || isProgrammaticRoute) {
      commit('GET_DATA_REFRESHED_TIME_SUCCEEDED', items)
    }

    return items
  },
  async requestAndRender({ dispatch, getters }, { endpointKey, params, query }) {
    const currentSelectedReport = await dispatch('getCurrentSelectedReport')
    const { parameters } = currentSelectedReport || {}

    const foundDateRangeParameters = filterDateRangeParameters(parameters)

    const getStoredItems = () => getters.currentReportKeyDataAll
    const getDefaultDateRangeByEndpointKey = () => {
      // const isTimeRollup = endpointKey === PATHS.INTERNAL.TIME_ROLLUP
      // return isTimeRollup ? [{ operator: 'range', value: 'month-to-date' }] : []
      const defaultValue = []
      return defaultValue
    }
    const getDataFromApi = async () => {
      const dateParameters = getDefaultDateRangeByEndpointKey()
      const { endDate, startDate } = makeDateRangeForApi(dateParameters)

      query.startDate = startDate
      query.endDate = endDate

      const response = await dispatch('request', { endpointKey, params, query })

      return dispatch('transform', { endpointKey, data: response, query })
    }

    const items = foundDateRangeParameters && foundDateRangeParameters.length ? await getDataFromApi() : getStoredItems()

    return dispatch('render', { endpointKey, items, params, query })
  },
  /**
   * Get data with additional progress updates for the view.
   * @param {Object} context
   * @param {Object} data
   */
  async setView({ dispatch, rootState }, { params, query }) {
    const root = { root: true }
    const { baseReportName, routeName } = params
    const endpointKey = baseReportName || routeName
    const { user } = rootState.auth

    dispatch('messages/showLoadingMessage', { componentName: endpointKey, text: 'Requesting data...' }, root)

    const data = await dispatch('request', { endpointKey, params, query, user })

    dispatch('messages/showLoadingMessage', { componentName: endpointKey, text: 'Transforming...' }, root)

    const items = await dispatch('transform', { endpointKey, data, query })

    await dispatch('render', { endpointKey, items, params, query })

    dispatch('messages/hideLoadingMessage', { componentName: endpointKey, text: 'Rendering...' }, root)
  },

  toggleModal({ commit, rootState }, { modalData, modalName, storeName }) {
    const propertyName = `showModal${modalName}`
    const modalState = !rootState[storeName][propertyName]

    if (modalData) {
      commit(`${storeName}/modalData`, modalData, { root: true })
    }

    commit(`${storeName}/${propertyName}`, modalState, { root: true })
  },

  /**
   * Fetches collection and saves it to store by its endpointKey.
   * @return items {Array} Result of requestCollection, which traces to tools/successHandler action.
   */
  async getCollectionByName({ dispatch, rootGetters }, { endpointKey, params, query, user }) {
    const storedCollection = rootGetters[`tools/${endpointKey}s`]

    const requestCollection = async () => {
      const data = await dispatch('request', { endpointKey, params, query, user })
      const items = await dispatch('transform', { endpointKey, data, selectedSearch: null })
      return dispatch('store', { endpointKey, items, query })
    }
    const getStoredCollection = () => {
      dispatch('updateView', { endpointKey, items: storedCollection })
      return storedCollection
    }

    return (storedCollection?.length && getStoredCollection()) || requestCollection()
  },

  transformInternalTeamItems(context, response) {
    return response.content
  },

  transformClientAssignments(context, response) {
    return response.content
  },
  transformProjectItems(context, response) {
    const transformItems = () => response.content.map((item) => {
      item.associationId = item.projectCode
      item.subId = '-1'

      item.baseReportName = 'project'
      item.reportPath = 'profile'
      item.routeName = 'internal'

      item.dataType = 'project'

      item.billingCode = item.projectCode

      return item
    })

    return (response.content && transformItems()) || response
  },
  transformServiceItems({ rootGetters }, response) {
    const currentColumnsByKey = rootGetters['tools/serviceColumnsByKey']
    const freezeItems = () => Promise.all(response.content.map((project) => transform.freeze(project, currentColumnsByKey)))
    return (response.content && freezeItems()) || response
  },
  transformTimeTypeItems(context, response) {
    const transformItems = () => response.content.map((entry) => {
      entry.dataType = 'time-type'
      return entry
    })

    return (response.content && transformItems()) || response
  },

  async transformTimeItems(context, content) {
    const transformItems = () => content.map(async (entry) => {
      entry.dataType = 'time-sheet'
      entry.time_entry_date = entry.timeEntryDate
      entry.person_id = entry.personId
      entry.client_id = entry.clientId
      entry.time_classification_id = entry.timeClassificationId
      entry.duration = entry.billableActionDuration
      entry.person_email = null
      entry.description = entry.workNote
      entry.project_id = entry.projectId
      entry.service_id = entry.serviceId

      return entry
    })

    return (content && Promise.all(transformItems())) || content
  },
  async transformTime(context, responses) {
    const [,,,, timeResponse] = responses
    const { content } = timeResponse
    const valid = Array.isArray(content)
    const transformer = () => content.map(async (entry) => {
      entry.dataType = 'time-sheet'
      entry.time_entry_date = entry.timeEntryDate
      entry.person_id = entry.personId
      entry.client_id = entry.clientId
      entry.time_classification_id = entry.timeClassificationId
      entry.duration = entry.billableActionDuration
      entry.person_email = null
      entry.description = entry.workNote
      entry.project_id = entry.projectId
      entry.service_id = entry.serviceId

      return entry
    })

    return (valid && await Promise.all(transformer())) || []
  },

  fetchTimeEntries(context, { endDate, startDate }) {
    // const email = rootGetters['auth/userEmail']
    // const body = {
    //   person_email: email,
    //   include_delegates: true,
    // }

    return api.getTimeEntriesByDateRange({ endDate, startDate })
  },
  fetchTimeEntriesForUser(context, { endDate, personId, startDate }) {
    // const body = {
    //   person_email: user.email,
    //   include_delegates: query.include_delegates || false,
    // }

    const getEntriesForUser = () => personId && api.getTimeEntriesByDateRangeForUser({ endDate, personId, startDate })
    const getEntriesAcrossUsers = () => api.getTimeEntriesByDateRange({ endDate, personId, startDate })

    return getEntriesForUser() || getEntriesAcrossUsers()
  },
  async fetchTime({ dispatch }, { apiQuery, user }) {
    const { endDate, personId, startDate } = apiQuery

    return Promise.all([
      dispatch('auth/getPersonUsers', { user: null }, { root: true }),
      dispatch('auth/getProjects', { user }, { root: true }),
      dispatch('auth/getServices', { user }, { root: true }),
      dispatch('auth/getTimeTypes', { user }, { root: true }),
      dispatch('fetchTimeEntriesForUser', { endDate, personId, startDate }),
    ])
  },

  async transformTimeRollup({ rootGetters }, responses) {
    const [,,,, timeResponse] = responses
    const { content } = timeResponse
    const columnsByKey = rootGetters['tools/timeRollupColumnsByKey']

    const formatAndNormalize = (item) => transform.freeze(item, columnsByKey)
    const items = content.map(async (entry) => {
      entry.time_entry_date = entry.timeEntryDate
      entry.person_id = entry.personId
      entry.client_id = entry.clientId
      entry.time_classification_id = entry.timeClassificationId
      entry.duration = entry.billableActionDuration
      entry.person_email = null
      entry.description = entry.workNote
      entry.project_id = entry.projectId
      entry.service_id = entry.serviceId

      return formatAndNormalize(entry)
    })

    return Promise.all(items)
  },
  fetchTimeRollup({ dispatch }, { apiQuery, user }) {
    // query.include_delegates = true
    return dispatch('fetchTime', { apiQuery, user })
  },

  async getDataRefresh({ commit }) {
    commit(actionNames.GET_DATA_REFRESH_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_DATA_REFRESH_FAILED, error)
      return error
    }

    const response = await api.getDataRefresh().catch(catcher)
    const { content, error } = response

    if (error) return error

    const [dataRefreshReport] = content

    commit(actionNames.GET_DATA_REFRESH_SUCCEEDED, dataRefreshReport)

    return dataRefreshReport
  },

  getSavedQueriesByBaseReportName({ getters }, { baseReportName, routeName }) {
    return getters.preparedReports.filter((query) => query.baseReportName === baseReportName && query.routeName === routeName)
  },
  async getNavigation({ dispatch, state, rootGetters }, navEndpoints) {
    const { admanagerRoutes, dashboardRoutes, deliveryRoutes, internalRoutes, programmaticRoutes } = state
    const topLevelNavigation = Object.keys(navEndpoints)

    const modelNavigation = topLevelNavigation.reduce((navArray, endpointName, navigationIndex) => {
      const endpoint = navEndpoints[endpointName]
      const { access, href } = endpoint
      const hasAccess = access(rootGetters)
      const getRestrictedRoutes = () => {
        // @TODO Determine whether it's more performant to combine these route arrays so indexOf always runs 1x.
        const isDashboardEndpoint = () => dashboardRoutes.indexOf(endpointName) !== -1
        const isDeliveryEndpoint = () => deliveryRoutes.indexOf(endpointName) !== -1
        const isAdmanagerEndpoint = () => admanagerRoutes.indexOf(endpointName) !== -1
        const isProgrammaticEndpoint = () => programmaticRoutes.indexOf(endpointName) !== -1
        const isInternalEndpoint = () => internalRoutes.indexOf(endpointName) !== -1
        const isTimeRollupEndpoint = () => endpointName === PATHS.INTERNAL.TIME_ROLLUP
        const isReportEndpoint = isTimeRollupEndpoint() || isDashboardEndpoint() || isDeliveryEndpoint() || isProgrammaticEndpoint() || isAdmanagerEndpoint() || isInternalEndpoint()

        const setStandardReportEndpoint = async () => {
          const { baseReportName, routeName } = endpoint
          const makeHref = () => {
            const defaultNetwork = rootGetters['auth/defaultNetwork']
            const { associationId, subId } = defaultNetwork || {}
            return href(associationId, subId)
          }
          const link = makeHref()
          const showReports = false

          const reports = showReports && await dispatch('getSavedQueriesByBaseReportName', { baseReportName, routeName })

          navArray.push({ ...endpoint, link, navigationIndex, reports })

          return navArray
        }
        const setCustomAnalyticEndpoint = () => {
          const link = href()
          const customAnalytics = false

          navArray.push({ ...endpoint, link, navigationIndex, customAnalytics })

          return navArray
        }
        const setDashboardEndpoint = async () => {
          const link = href()
          const networks = false

          navArray.push({ ...endpoint, link, navigationIndex, networks })

          return navArray
        }
        const setLinkedEndpoint = async () => {
          // Example: const { associationId, subId } = state; const link = href(associationId, subId);
          const link = href()

          navArray.push({ ...endpoint, link, navigationIndex })

          return navArray
        }
        const setTimeEndpoint = () => {
          const link = href()
          const times = false

          navArray.push({ ...endpoint, link, navigationIndex, times })

          return navArray
        }

        const pushTimeEndpoint = () => endpointName === PATHS.INTERNAL.TIME_SHEET && setTimeEndpoint()
        const pushDashboardEndpoint = () => endpointName === PATHS.DASHBOARD.BASE && setDashboardEndpoint()
        const pushCustomAnalyticEndpoint = () => endpointName === PATHS.CUSTOM_ANALYTICS.BASE && setCustomAnalyticEndpoint()
        const pushStandardReportEndpoint = () => isReportEndpoint && setStandardReportEndpoint()

        return pushDashboardEndpoint() || pushCustomAnalyticEndpoint() || pushTimeEndpoint() || pushStandardReportEndpoint() || setLinkedEndpoint()
      }

      if (hasAccess) getRestrictedRoutes()

      return navArray
    }, [])

    return modelNavigation.sort((a, b) => ((a.navigationIndex > b.navigationIndex && 1) || -1))
  },
  async setNavigation({ commit, dispatch }, navEndpoints) {
    const results = await dispatch('getNavigation', navEndpoints)
    commit('navigation', results)
  },

  getCurrentSelectedReport({ getters }) {
    return getters.currentSelectedReport
  },

  /**
   * Creates search options for the given endpoint.
   * @param context
   * @param {Object} endpoint
   * @returns {function(Object[], Object): Promise<Object[]>}
   */
  mapSearchOptions({ getters, state }, endpoint) {
    const { reportPathDefault } = state

    return async (optionsArray, option) => {
      const { filter } = endpoint
      const skipFilter = typeof filter !== 'function'
      const include = skipFilter || filter(option)

      if (include) {
        const { baseReportName, routeName } = endpoint
        const excludedReports = ['organization', 'person', 'project']
        const makeTimeOptions = baseReportName === PATHS.INTERNAL.TIME_SHEET
        const makeProfileOptions = () => excludedReports.indexOf(baseReportName) !== -1 && option.associationId !== 'all-associations'
        const navKey = baseReportName || routeName
        const foundNavigationItem = getters.navigationByKey[navKey]

        if (makeTimeOptions) {
          const reports = [
            {
              text: 'This Week',
              value: 'this-week',
            },
            {
              text: 'Last Week',
              value: 'last-week',
            },
            {
              text: 'Next Week',
              value: 'next-week',
            },
            {
              text: 'Relative Week',
              value: 'relative-week',
            },
          ]

          reports.forEach((report) => {
            const { text, value } = report
            const mapOption = search.mapOption(endpoint)
            const searchOption = mapOption(option, { reportPath: value, reportLabel: text })

            searchOption.href = getters.makeSearchHref(reportPathDefault, foundNavigationItem, searchOption)
            searchOption.parameters = []

            optionsArray.push(searchOption)
          })
        } else if (makeProfileOptions()) {
          const mapOption = search.mapOption(endpoint)
          const searchOption = mapOption(option, { reportLabel: 'Profile' })

          searchOption.href = getters.makeSearchHref(reportPathDefault, foundNavigationItem, searchOption)
          searchOption.parameters = []

          optionsArray.push(searchOption)
        } else {
          const currentEndpointReports = getters.preparedReports.filter((report) => report.baseReportName === baseReportName && report.routeName === routeName)
          const reportOptions = getters.makeReportOptionsForTeam(option, currentEndpointReports)
          const shouldMapReportOptions = reportOptions && reportOptions.length

          if (shouldMapReportOptions) {
            reportOptions.forEach((reportOption) => {
              const { reportPath } = reportOption
              const mapOption = search.mapOption(endpoint)
              const searchOption = mapOption(option, reportOption)

              searchOption.href = getters.makeSearchHref(reportPath, foundNavigationItem, reportOption)

              optionsArray.push(searchOption)
            })
          } else {
            const mapOption = search.mapOption(endpoint)
            const reportOption = { reportPath: reportPathDefault }
            const searchOption = mapOption(option, reportOption)

            searchOption.href = getters.makeSearchHref(reportPathDefault, foundNavigationItem, searchOption)
            searchOption.parameters = []

            optionsArray.push(searchOption)
          }
        }
      }

      return optionsArray
    }
  },
  /**
   * Fetches search data for each allowed endpoint.
   * @param {VuexContext} context
   * @returns {Promise<{customAnalytic: Promise<Object[]>, person: Promise<Object[]>, organization: Promise<Object[]>, project: Promise<Object[]>, team: Promise<Object[]>}>}
   */
  async getSearchData({ dispatch, rootGetters, rootState }) {
    const isInternalUser = rootGetters['auth/isInternalUser']
    const { user } = rootState.auth
    const params = {
      baseReportName: 'organization',
      routeName: 'internal',
    }
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    const externalRequests = () => [
      dispatch('auth/getOrganizations', { params, query, user }, { root: true }),
    ]
    const internalRequests = () => [
      dispatch('auth/getOrganizations', { params, query, user }, { root: true }),
      dispatch('auth/getPersonUsers', { user: null }, { root: true }),
      api.getProjects(),
    ]
    const promises = (isInternalUser && internalRequests()) || externalRequests()
    const [organization, person, project] = promises
    const team = organization.then(() => rootGetters['tools/publishersAsTeams'])

    const customAnalytic = organization.then((orgs) => orgs.reduce((customAnalyticOptions, org) => {
      if (org.hasCustomAnalytics && org.customAnalyticsActive?.length) {
        org.customAnalyticsActive.forEach((cReport) => {
          const { billingCode, path } = cReport
          cReport.href = `/custom-analytics/${path}/${billingCode}`
          customAnalyticOptions.push(cReport)
        })
      }
      return customAnalyticOptions
    }, []))

    const associations = organization.then((orgs) => {
      const allAssociations = [
        {
          associationId: 'all-associations',
          billingCode: 'all-associations',
          orgName: 'All Associations',
          personName: 'All Associations',
          projectName: 'All Associations',
          timeEntryId: 'all-associations',
          subId: 'all-secondary',
        },
      ]
      return allAssociations.concat(orgs)
    })

    const personAssociations = Promise.all([associations, person]).then(([assoc, people]) => [].concat(assoc, people))
    const projectAssociations = Promise.all([associations, project]).then(([assoc, projects]) => [].concat(assoc, projects))
    const timeAssociations = Promise.resolve([
      {
        associationId: 'all-associations',
        billingCode: 'all-associations',
        personNameFirst: user.personNameFirst,
        personNameLast: user.personNameLast,
        timeEntryId: 'all-associations',
        subId: 'all-secondary',
      },
    ])

    return { associations, customAnalytic, organization, person, personAssociations, project, projectAssociations, team, timeAssociations }
  },
  async getSearchOptionsForEachEndpoint({ dispatch }) {
    const searchData = await dispatch('getSearchData')
    const { associations, customAnalytic, organization, personAssociations, projectAssociations, team, timeAssociations } = searchData

    return {
      [PATHS.CUSTOM_ANALYTICS.BASE]: customAnalytic,
      [PATHS.DASHBOARD.BASE]: team,
      [PATHS.DELIVERY.DISCREPANCY]: team,
      [PATHS.DELIVERY.PACING]: team,
      [PATHS.INTERNAL.ORGANIZATION]: associations,
      [PATHS.INTERNAL.PERSON]: personAssociations,
      [PATHS.INTERNAL.PROJECT]: projectAssociations,
      [PATHS.INTERNAL.TIME_ROLLUP]: associations,
      [PATHS.INTERNAL.TIME_SHEET]: timeAssociations,
      [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: organization,
      [PATHS.PROGRAMMATIC.ADX_HEALTH]: organization,
      [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: organization,
      team,
    }
  },
  isAllowedSearchEndpoint({ rootGetters }, { endpointName, navEndpoints }) {
    const navEndpoint = navEndpoints[endpointName]
    return search.hasEndpointAccess(rootGetters, navEndpoint)
  },
  async setSearchOptions({ dispatch, state }, searchReduce) {
    const searchInfo = await searchReduce
    const { Fuse, navEndpoints, searchEndpointReport } = searchInfo
    const { endpointName, results, searchData } = searchEndpointReport
    const { SEARCH_OPTIONS } = state
    const hasAccess = await dispatch('isAllowedSearchEndpoint', { endpointName, navEndpoints })

    if (!hasAccess) return results

    const searchEndpoint = results.endpoints[endpointName]
    const { savedAs } = searchEndpoint

    if (savedAs) {
      const response = await searchData[savedAs]
      const { content = response, error } = response || {}

      if (error || !Array.isArray(content)) {
        results.endpoints[endpointName].error = response
      } else {
        const mapper = await dispatch('mapSearchOptions', searchEndpoint)
        const mapSearchOptions = async (optionsArrayPromise, option) => {
          const optionsArray = await optionsArrayPromise
          return mapper(optionsArray, option)
        }
        const searchOptions = await content.reduce(mapSearchOptions, Promise.resolve([]))

        results.endpoints[endpointName].itemsArray = searchOptions
        results.endpoints[endpointName].items = new Fuse(searchOptions, SEARCH_OPTIONS)
      }
    }

    return results
  },
  async setSearchOptionsOnEndpoints({ dispatch }, searchOptionsAndEndpoints) {
    const { default: Fuse } = await import('fuse.js')
    const { allowedEndpoints, navEndpoints } = searchOptionsAndEndpoints
    const { endpointNames, searchData, endpoints, total } = allowedEndpoints
    const setSearchOptions = async (searchEndpointPromise, endpointName) => {
      const results = await searchEndpointPromise
      const searchEndpointReport = { endpointName, results, searchData }

      return dispatch('setSearchOptions', { Fuse, navEndpoints, searchEndpointReport })
    }
    const searchEndpoint = { endpointNames, endpoints, total }

    return endpointNames.reduce(setSearchOptions, Promise.resolve(searchEndpoint))
  },
  /**
   * Updates store with search data required to render
   *
   * @param {*} context { commit, dispatch }
   * @param {*} searchOptionsAndEndpoints { endpointNames: [], searchData: {}, searchEndpoints: {} }
   */
  async commitSearchData({ commit, dispatch }, searchOptionsAndEndpoints) {
    commit('searchData', searchOptionsAndEndpoints.allowedEndpoints.searchData)

    return dispatch('setSearchOptionsOnEndpoints', searchOptionsAndEndpoints)
  },
  async setSearchData({ dispatch }, commitSearchData) {
    const { allowedEndpoints, navEndpoints } = commitSearchData
    const { catcher, endpointNames, endpoints, total } = allowedEndpoints

    const searchData = await dispatch('getSearchOptionsForEachEndpoint').catch(catcher)

    if (!searchData) return []

    return dispatch('commitSearchData', {
      allowedEndpoints: { endpointNames, searchData, endpoints, total },
      navEndpoints,
    })
  },
  /**
   * Initializes render process and oversees state
   *
   * @param {Object} context { commit, dispatch } Vue store
   * @param {Object} commitSearchData { endpointNames: [], searchResults: {} }
   */
  async renderSearch({ commit, dispatch }, commitSearchData) {
    commitSearchData.catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_SEARCH_FAILED, error)
      return error
    }

    commit(actionNames.GET_SEARCH_REQUESTED)

    const options = await dispatch('setSearchData', commitSearchData)

    commit(actionNames.GET_SEARCH_SUCCEEDED, options)

    return options
  },
  getAllowedEndpoints({ rootGetters }, navEndpoints) {
    const { endpoints: searchEndpoints } = search
    const total = { items: 0, recent: 0, results: 0 }

    const endpointNames = Object.keys(searchEndpoints).filter((endpointName) => {
      const navEndpoint = navEndpoints[endpointName]
      return search.hasEndpointAccess(rootGetters, navEndpoint)
    })

    const endpoints = endpointNames.reduce((allowedEndpoints, endpointName) => {
      allowedEndpoints[endpointName] = searchEndpoints[endpointName]
      return allowedEndpoints
    }, {})

    return { endpointNames, endpoints, total }
  },
  async setupNav({ commit, dispatch }) {
    const navEndpoints = navigation().endpoints
    const allowedEndpoints = await dispatch('getAllowedEndpoints', navEndpoints)

    commit('searchResults', allowedEndpoints)

    dispatch('setNavigation', navEndpoints)

    return dispatch('renderSearch', { allowedEndpoints, navEndpoints })
  },
  setSearchAdvancedResults({ state }) {
    const {
      didLoadSearchFail,
      isLoadingSearch,
      searchResults,
      searchTerm,
      SEARCH_TEXT_KEY,
    } = state
    const { endpointNames, endpoints: searchEndpoints, total } = searchResults

    const readyToSearch = !isLoadingSearch && !didLoadSearchFail

    if (!readyToSearch) {
      return { endpointNames, endpoints: searchEndpoints, total }
    }

    const getEndpoint = search.getEndpoints(SEARCH_TEXT_KEY, searchTerm, total)
    const endpoints = endpointNames.reduce(getEndpoint, searchEndpoints)

    return { endpointNames, endpoints, total }
  },
  async setSearchAdvanced({ commit, dispatch }, searchTerm) {
    commit(actionNames.UPDATE_SEARCH_TERM, searchTerm)

    return dispatch('setSearchAdvancedResults', searchTerm)
  },

  getSearchSimple({ state }, { hasSearchStarted, searchOptions, searchTerm, textKey }) {
    const { didLoadSearchFail, isLoadingSearch, SEARCH_TEXT_ALT } = state
    const { endpointNames, endpoints: searchEndpoints, total } = searchOptions
    const SEARCH_CHARACTER_MINIMUM = 1

    const hasStartedSearching = () => hasSearchStarted && searchTerm.length
    const readyToSearch = !isLoadingSearch && !didLoadSearchFail && (hasStartedSearching() || searchTerm.length > SEARCH_CHARACTER_MINIMUM)

    if (!readyToSearch) {
      return { endpointNames, endpoints: searchEndpoints, total }
    }

    const searchTextKey = textKey || SEARCH_TEXT_ALT
    const getEndpoint = search.getEndpoints(searchTextKey, searchTerm, total)
    const endpoints = endpointNames.reduce(getEndpoint, searchEndpoints)

    return { endpointNames, endpoints, total }
  },

  updateRoute({ dispatch }, searchOption) {
    const { associationId, baseReportName = null, reportPath, routeName = null, subId } = searchOption
    const catcher = (error) => dispatch('messages/showErrorMessage', { error, baseReportName }, { root: true })

    const newRoute = {
      params: { baseReportName, routeName },
      query: { action: 'read', associationId, reportPath, subId },
    }

    if (searchOption.timeShift && baseReportName === PATHS.INTERNAL.TIME_SHEET) {
      newRoute.query.timeShift = searchOption.timeShift
    }

    Router.push(newRoute).catch(catcher)
  },

  getDefaultTeamId(context, teams) {
    const getAllTeams = teams.find((team) => team.id === '-1')

    if (getAllTeams) return 'all-secondary'

    const [primaryTeam = {}] = teams
    const { id: teamId = 'all-secondary' } = primaryTeam

    return teamId
  },

  /**
   * Normalizes params for the given route. Output is used to find the selectedSearch among search options.
   * @param {Object} context
   * @param {function} context.dispatch
   * @param {Object} context.getters
   * @param {Object} context.rootGetters
   * @param {Object} params
   * @param {Object[]} params.network A user's network array, i.e. [{ organization: { code, id, networkCode }, team: [{ code, id }] }]
   * @param {String|undefined} params.reportPath
   * @param {Number|String|undefined} params.associationId
   * @param {String|null|undefined} params.baseReportName
   * @param {String|null|undefined} params.routeName
   * @param {Number|String|undefined} params.subId
   * @param {Number|String|undefined} params.networkCode Specific to the Admanager route.
   * @param {Number|String|undefined} params.orderId Specific to the Admanager route.
   * @returns {Promise<Object>} Params the views can use to get a selectedSearch
   */
  async getParams({ getters, rootGetters }, { params, query }) {
    const inDemoMode = rootGetters['auth/inDemoMode']
    const isInternalUser = rootGetters['auth/isInternalUser']
    const { isAdManagerRoute, isInternalRoute, isAssociationIdRequiredOnRoute } = getters

    const { associationId: paramAssociationId, baseReportName, reportPath: paramReportPath, routeName, subId: paramSubId } = params
    const { associationId: queryAssociationId, metricPath: queryMetricPath, reportPath: queryReportPath, subId: querySubId } = query || {}

    const reportPath = queryReportPath || paramReportPath
    const associationId = queryAssociationId || paramAssociationId
    const subId = querySubId || paramSubId || 'all-secondary'

    const makeQuery = (newReportPath, newAssociationId, newSubId) => {
      const newQuery = {
        action: query.action,
        reportPath: newReportPath,
        associationId: newAssociationId,
        subId: newSubId,
      }

      if (queryMetricPath) newQuery.metricPath = queryMetricPath

      return newQuery
    }
    const makeParams = (newBaseReportName, newRouteName) => ({
      baseReportName: newBaseReportName,
      routeName: newRouteName,
    })

    const setNetworkParams = () => ({
      params: makeParams(baseReportName, routeName),
      query: makeQuery(reportPath, associationId, subId),
    })
    const setInternalParams = () => {
      const newReportPath = reportPath || 'all-data'
      const newAssociationId = !inDemoMode ? associationId : 'DEMO2021'
      const newSubId = !inDemoMode ? subId : '-1'

      return {
        params: makeParams(baseReportName, routeName),
        query: makeQuery(newReportPath, newAssociationId, newSubId),
      }
    }
    const setGivenParams = () => ({
      params: makeParams(baseReportName, routeName),
      query: makeQuery(reportPath, associationId, subId),
    })

    const setReportParams = async () => {
      const { isAssociationIdBillingCode, isAssociationIdNumerical } = getters

      const setDemoNetworkParams = () => {
        const newAssociationId = 'DEMO2021'
        const newBaseReportName = null
        const newReportPath = null
        const newRouteName = 'dashboard'
        const newSubId = '-1'

        return {
          params: makeParams(newBaseReportName, newRouteName),
          query: makeQuery(newReportPath, newAssociationId, newSubId),
        }
      }
      const setBillingCodeParams = () => {
        const newReportPath = reportPath || 'all-data'
        const newAssociationId = associationId
        const newSubId = subId

        return {
          params: makeParams(baseReportName, routeName),
          query: makeQuery(newReportPath, newAssociationId, newSubId),
        }
      }
      const setBillingCodeParamsFromPublisherId = () => {
        const { teamsByReportCode } = getters
        const team = teamsByReportCode[`${associationId}/${subId}`]
        const { billingCode, teamId } = team

        const newReportPath = reportPath || 'all-data'
        const newAssociationId = billingCode
        const newSubId = teamId

        return {
          params: makeParams(baseReportName, routeName),
          query: makeQuery(newReportPath, newAssociationId, newSubId),
        }
      }
      const setBillingCodeParamsFromDefaultNetwork = async () => {
        const defaultNetwork = rootGetters['auth/defaultNetwork']
        const { associationId: newAssociationId, subId: newSubId } = defaultNetwork

        const newReportPath = reportPath || 'all-data'

        return {
          params: makeParams(baseReportName, routeName),
          query: makeQuery(newReportPath, newAssociationId, newSubId),
        }
      }

      const useDemoModeParams = () => inDemoMode && setDemoNetworkParams()
      const useBillingCode = () => associationId && isAssociationIdBillingCode && setBillingCodeParams()
      const usePublisherId = () => associationId && isAssociationIdNumerical && setBillingCodeParamsFromPublisherId()
      const useGivenAssociationId = () => associationId && setGivenParams()
      const useDefaultNetwork = () => !associationId && setBillingCodeParamsFromDefaultNetwork()

      return useDemoModeParams() || useBillingCode() || usePublisherId() || useGivenAssociationId() || useDefaultNetwork()
    }

    const setAdmanagerRouteParams = () => isAdManagerRoute && setNetworkParams()
    const setInternalRouteParams = () => isInternalRoute && setInternalParams()
    const setReportRouteParamsForInternals = () => isInternalUser && isAssociationIdRequiredOnRoute && setGivenParams()
    const setReportRouteParamsForExternals = () => isAssociationIdRequiredOnRoute && setReportParams()
    const setReportRouteParams = () => setReportRouteParamsForInternals() || setReportRouteParamsForExternals()

    return setAdmanagerRouteParams() || setInternalRouteParams() || setReportRouteParams() || setGivenParams()
  },

  async selectSearchItem({ commit, dispatch }, selection) {
    commit(actionNames.UPDATE_SEARCH_TERM, '')
    commit(actionNames.UPDATE_RECENT_SEARCHES, selection)

    dispatch('setNavigation', navigation().endpoints)

    return dispatch('updateRoute', selection)
  },

  editBookmark({ getters }, { selectedItem, selectedReport }) {
    const { baseReportName, reportPath, routeName } = selectedReport
    const navKey = baseReportName || routeName
    const navigationItem = getters.navigationByKey[navKey]

    selectedItem.href = getters.makeSearchHref(reportPath, navigationItem, selectedReport)
    selectedItem.reportLabel = getters.makeSearchLabel(navigationItem, selectedReport)

    selectedItem.associationId = selectedReport.associationId
    selectedItem.baseReportName = selectedReport.baseReportName
    selectedItem.reportPath = selectedReport.reportPath
    selectedItem.reportCode = selectedReport.reportCode
    selectedItem.routeName = selectedReport.routeName
    selectedItem.subId = selectedReport.subId
  },
  editNetwork({ getters }, { selectedItem, selectedReport }) {
    const { baseReportName, reportPath, routeName } = selectedReport
    const navKey = baseReportName || routeName
    const navigationItem = getters.navigationByKey[navKey]

    selectedItem.href = getters.makeSearchHref(reportPath, navigationItem, selectedReport)
    selectedItem.reportLabel = selectedReport.text

    selectedItem.associationId = selectedReport.associationId
    selectedItem.reportCode = selectedReport.reportCode
    selectedItem.subId = selectedReport.subId

    selectedItem.baseReportName = selectedReport.baseReportName
    selectedItem.reportPath = selectedReport.reportPath
    selectedItem.routeName = selectedReport.routeName
  },

  toggleRows({ commit, state }, groupName) {
    const showRows = [...state.showRows]

    if (showRows.includes(groupName)) {
      const index = showRows.findIndex((v) => v === groupName)

      showRows.splice(index, 1)
    } else {
      showRows.push(groupName)
    }

    commit('showRows', showRows)
  },
  toggleGroups({ commit, dispatch, getters }) {
    const { currentGroups } = getters
    const timeout = 0

    commit('hasClickedToggler', true)
    commit('isTogglingTables', true)

    const getVisibleGroups = (currentGroup) => currentGroup.items.length
    const currentVisibleGroups = currentGroups.filter(getVisibleGroups)

    currentVisibleGroups.forEach((sortedGroup) => {
      setTimeout(() => {
        dispatch('toggleRows', sortedGroup.groupName)
      }, timeout)
    })

    fireanalytics.event('goal_completion', {
      category: 'Group',
      description: 'Toggle Groups',
      label: `Toggle ${currentVisibleGroups.length} Groups`,
    })

    commit('hasClickedToggler', false)
    commit('isTogglingTables', false)
  },

  setColumnVisibility(context, { action, column, visibilityReport }) {
    const { currentProperties, newProperties } = visibilityReport
    const { key, required } = column
    const notTraffickerColumn = !/trafficker/.test(key)
    const isNameColumn = notTraffickerColumn && /Code\b|Id\b|Name\b/i.test(key)
    const isIdColumn = notTraffickerColumn && /Code\b|Id\b|Name\b/i.test(key)
    const hasPair = isNameColumn || isIdColumn
    const getSiblings = () => {
      const keyRoot = hasPair ? key.replace(/Code\b|Id\b|Name\b/i, '') : null
      return keyRoot ? [`${keyRoot}Code`, `${keyRoot}Id`, `${keyRoot}Name`] : []
    }
    const keySiblings = hasPair ? getSiblings() : []

    if (required) return column

    const isUngroupingAll = !newProperties
    const previouslyGroupedByColumn = currentProperties?.includes(key) || keySiblings.find((keySibling) => currentProperties?.includes(keySibling))
    const isGroupingByColumn = action === 'groupBy' && (newProperties.includes(key) || keySiblings.find((keySibling) => newProperties?.includes(keySibling)))
    const isUngroupingSingleColumn = previouslyGroupedByColumn && !isGroupingByColumn

    if (isUngroupingAll && previouslyGroupedByColumn) {
      column.visible = true
    } else if (isUngroupingSingleColumn) {
      column.visible = true
    } else if (isGroupingByColumn) {
      column.visible = false
    }

    const actionMap = {
      false: 'Hide',
      true: 'Show',
    }

    fireanalytics.event('goal_completion', {
      category: 'Column',
      description: 'Column Visibility',
      label: `${actionMap[column.visible]} Column "${column.columnName}"`,
    })

    return column
  },

  async setColumnByGrouping({ commit, dispatch, getters, state }, groupByReport) {
    const { tableColumns } = getters
    const { currentGroupByProperties, groupByProperties } = groupByReport
    const visibilityReport = { currentProperties: currentGroupByProperties, newProperties: groupByProperties }
    const setColumnVisibility = async (column) => dispatch('setColumnVisibility', { action: 'groupBy', column, visibilityReport })
    const newColumns = await Promise.all(tableColumns.map(setColumnVisibility))

    commit('columns', { [state.baseReportName]: newColumns })

    return newColumns
  },

  sortGroupedItems({ state }, { columnKey, columnDirection, columnFormat, items }) {
    const sortDsc = columnDirection === state.tableSortMap.DSC
    const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive
    const isDateColumn = columnFormat === 'date'

    const valueGetter = (item) => {
      const getValue = () => {
        const value = !isDateColumn && item.normalized ? item.normalized[columnKey] : item[columnKey]
        const sortArrayValues = () => (sortDsc ? value.sort().reverse() : value.sort())
        return Array.isArray(value) ? sortArrayValues()[0] : value?.value || value
      }
      const getDate = (value) => dates.getUnixDate(value)
      return isDateColumn ? getDate(getValue()) : getValue()
    }

    return sorter(items, valueGetter)
  },
  sortGroupedItemsByAllColumns({ dispatch, getters }, { columnsByKey, unsortedItems }) {
    const sortItems = async (itemsPromise, columnKey, index) => {
      const items = await itemsPromise
      const columnDirection = getters.sortByDirections[index]
      const columnFormat = columnsByKey[columnKey]?.formatAs

      return dispatch('sortGroupedItems', { columnDirection, columnFormat, columnKey, items })
    }

    return getters.sortByKeys.reduceRight(sortItems, Promise.resolve(unsortedItems))
  },
  exportAs({ dispatch }, exportReport) {
    const { exportFormat = 'csv' } = exportReport

    if (exportFormat === 'xlsx') {
      fireanalytics.event('goal_completion', {
        category: 'Export',
        description: 'Export As XLSX',
        label: 'Export Succeeded',
      })

      return true
    }

    return dispatch('exportAsCsv', exportReport)
  },
  async exportAsCsv({ commit, dispatch, getters, state }, exportReport) {
    fireanalytics.event('goal_initialization', {
      category: 'Export',
      description: 'Export As CSV',
      label: 'Export Requested',
    })

    const { exportColumns, exportGroups, exportName } = exportReport
    const { baseReportName } = state
    const { currentColumns, currentGroups } = getters
    const groups = exportGroups || currentGroups
    const columns = exportColumns || currentColumns
    const columnsByKey = getters.columnsByKeyReducer(exportColumns)
    const promiseReportItemsByGroup = groups.reduce((previousValue, currentGroup) => {
      const { items: currentItems, rows: currentRows } = currentGroup
      const unsortedItems = currentItems || currentRows
      const promiseSortedRows = dispatch('sortGroupedItemsByAllColumns', { columnsByKey, unsortedItems })

      return previousValue.concat(promiseSortedRows)
    }, [])
    const catcher = (err) => {
      const error = http.catch(err)
      const { message } = error

      fireanalytics.event('exception', {
        category: 'Export',
        description: 'Export As CSV',
        label: `Export As CSV Failed with Error "${message}"`,
      })

      const viewError = { message, status: 'error' }

      commit('snackbar', true)
      commit('snack', viewError)

      return error
    }
    const sortedItemsByGroup = await Promise.all(promiseReportItemsByGroup).catch(catcher)
    const reportItems = sortedItemsByGroup.reduce((flatArray, items) => flatArray.concat(items), [])

    const headers = columns
      .map((column) => {
        const { columnName } = column

        return `"${columnName}"`
      })
      .join(',')

    const cells = (item) => {
      const formatCells = (column) => {
        const cellValue = column.columnValue(item)
        return `"${cellValue}"`
      }

      return columns.map(formatCells).join(',')
    }

    const getDateAsUnix = (row) => dates.get(row.date).unix()
    const sourceRows = /creatives_/.test(exportName)
      ? _.sortDscByCaseInsensitive(reportItems, getDateAsUnix)
      : reportItems
    const rows = sourceRows.map(cells)
    const csvRows = rows.join('\n')
    const encoding = 'data:text/csv;charset=utf-8'
    const data = `${encoding},${encodeURIComponent(`${headers}\n${csvRows}`)}`
    const link = document.createElement('a')
    const exportDate = dates.getFormattedDate(undefined, 'YYYY-MM-DD')
    const fileExtension = 'csv'
    const fileName = `oao_${exportName || baseReportName}_on_${exportDate}`

    link.setAttribute('href', data)
    link.setAttribute('download', `${fileName}.${fileExtension}`)

    link.click()

    fireanalytics.event('goal_completion', {
      category: 'Export',
      description: 'Export As CSV',
      label: `Export As CSV Succeeded for Report "${exportName}"`,
    })
  },

  async setColumnByMerging({ commit, dispatch, getters, state }, mergeByReport) {
    const { columns, baseReportName } = state
    const { currentColumns = columns[baseReportName] } = getters
    const setColumns = async () => {
      const { currentMergeByProperties, mergeByProperties } = mergeByReport
      const visibilityReport = { currentProperties: currentMergeByProperties, newProperties: mergeByProperties }

      const newColumns = await Promise.all(currentColumns.map(async (column) => {
        const action = 'mergeBy'
        return dispatch('setColumnVisibility', { action, column, visibilityReport })
      }))

      commit('columns', { [baseReportName]: newColumns })

      return newColumns
    }

    return currentColumns && setColumns()
  },

  refreshApiData({ dispatch, state }, report) {
    const { associationId, baseReportName, subId } = report
    const { filterData, searchResults, selected: selectedSearch } = state
    const { route } = searchResults.endpoints[baseReportName]
    const dateRange = filterData[baseReportName].date
    const query = {
      params: { dateRange, associationId, baseReportName, subId },
      selectedSearch,
    }

    return dispatch(`${route}/getData`, query, { root: true })
  },

  hideSavedQueryInfo({ commit }) {
    commit('isInfoModalVisible', false)
    commit('infoModal', null)
  },
  showSavedQueryInfo({ commit }, summary) {
    commit('infoModal', summary)
    commit('isInfoModalVisible', true)
  },
  async unsetSavedQuery({ commit, dispatch }, baseReportName) {
    dispatch('hideSavedQueryInfo')

    commit('removeSavedFilter', baseReportName)
  },

  displayFilter({ commit, state }, displayFilter) {
    const isSet = [true, false].includes(displayFilter)
    const { displayFilter: savedState } = state
    const newState = isSet ? displayFilter : !savedState

    commit('displayFilter', newState)

    return newState
  },
  toggleAdvancedTools({ commit, state }, displayAdvancedTools) {
    const isSet = [true, false].includes(displayAdvancedTools)
    const { displayAdvancedTools: savedState } = state
    const newState = isSet ? displayAdvancedTools : !savedState

    commit('displayAdvancedTools', newState)

    return newState
  },

  async getApiSummary({ state }, summary) {
    const { catcher, get, config } = summary
    const { networkCode, publisherId, teamId } = config
    const {
      items: {
        orders: {
          [publisherId]: { [teamId]: orders },
        },
      },
    } = state

    const summaryLineItems = await get(config).catch(catcher)
    const { content: lineItems, error, totalCount: total } = summaryLineItems

    if (error) return { items: summaryLineItems, total }

    const items = transform.pacing(networkCode, { lineItems, orders })
    const newData = { error: false, items, total }

    return { ...summary, ...newData }
  },
  async getSummary({ commit, dispatch }, { items, baseReportName, summary }) {
    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_SUMMARY_FAILED, { baseReportName, summary: { error } })
      return error
    }

    const { config } = summary
    const { params: filterData, useApi } = config
    const request = useApi ? dispatch('getApiSummary', summary) : dispatch('filterItems', { filterData, items })
    const summaryResponse = await request.catch(catcher)
    const { error } = summaryResponse
    const { content } = (useApi && summaryResponse) || { content: summaryResponse }

    if (error) {
      summary.error = error
    } else {
      summary.items = content
      summary.total = content.length
    }

    return summary
  },
  async summary({ commit, dispatch }, { items, baseReportName, summary }) {
    commit(actionNames.GET_SUMMARY_REQUESTED, { baseReportName, summary })

    const summaryReport = await dispatch('getSummary', { items, baseReportName, summary })

    if (summaryReport.error) {
      commit(actionNames.GET_SUMMARY_FAILED, { baseReportName, summary: summaryReport })
    } else {
      commit(actionNames.GET_SUMMARY_SUCCEEDED, { baseReportName, summary: summaryReport })
    }

    return summaryReport
  },
  updateSavedFilterSummaries({ dispatch }, { items, baseReportName, summaries }) {
    const summaryKeys = Object.keys(summaries)

    return summaryKeys.reduce(async (summaryResponses, summaryKey) => {
      const responses = await summaryResponses
      const summary = responses[summaryKey]

      responses[summaryKey] = await dispatch('summary', { items, baseReportName, summary })

      return responses
    }, Promise.resolve(summaries))
  },
  updateSimpleSummaries({ commit }, { baseReportName, summaries }) {
    const summaryKeys = Object.keys(summaries)

    return summaryKeys.reduce((summariesReport, summaryKey) => {
      const summaryReport = summariesReport[summaryKey]

      summaryReport.isLoading = false

      // @TODO Consider scenarios where summary calculations fail.
      commit(actionNames.GET_SUMMARY_SUCCEEDED, { baseReportName, summary: summaryReport })

      return summariesReport
    }, summaries)
  },
  updateTimeSummaries({ dispatch }, { items, baseReportName, summaries }) {
    const timeSummaries = transform[PATHS.INTERNAL.TIME_SHEET](items, summaries)

    return dispatch('updateSimpleSummaries', { baseReportName, summaries: timeSummaries })
  },
  updateCalculatedSummaries({ dispatch }, { baseReportName, items, summaries }) {
    const programmaticSummaries = transform.metrics(items, summaries)

    return dispatch('updateSimpleSummaries', { baseReportName, summaries: programmaticSummaries })
  },
  getMetrics(context, { items, summaries }) {
    const summaryKeys = Object.keys(summaries)
    const currentSummaries = summaryKeys.reduce((summaryDictionary, summaryKey) => {
      summaryDictionary[summaryKey] = { ...summaries[summaryKey], items: [], total: 0 }
      return summaryDictionary
    }, {})

    return transform.metrics(items, currentSummaries)
  },
  /**
   * Constructs a SummarySet from the report items, then sets the SummarySet on the state.
   * @param {object} context - Current Vuex store
   * @param {object} report { items: object[], summaries: SummarySet }
   * @return {Promise<SummarySet>} Promise<SummarySet>
   */
  async getSummaries({ dispatch, getters, state }, report) {
    const { baseReportName } = state
    const { isReportRoute } = getters
    const isTimeRoute = () => [PATHS.INTERNAL.TIME_ROLLUP, PATHS.INTERNAL.TIME_SHEET].includes(baseReportName)
    const { items, summaries } = report

    const getTimeSummaries = () => isTimeRoute() && dispatch('updateTimeSummaries', { items, baseReportName, summaries })
    const getProgrammaticSummaries = () => isReportRoute && dispatch('updateCalculatedSummaries', { items, baseReportName, summaries })

    return getTimeSummaries() || getProgrammaticSummaries() || {}
  },
  getSummaryRequests({ getters }, summariesConfig) {
    const { currentFilterData, isProgrammaticRoute, isAssociationIdRequiredOnRoute, isReportRoute } = getters
    const { isAlert, networkCode, associationId, baseReportName, subId, summaries } = summariesConfig
    const defaultSummaries = summaries[baseReportName].defaults || summaries[baseReportName]
    const summaryKeys = Object.keys(defaultSummaries)
    const reportSummaries = Object.keys(defaultSummaries).reduce((newSummaries, summaryKey) => {
      newSummaries[summaryKey] = defaultSummaries[summaryKey]
      return newSummaries
    }, {})

    return summaryKeys.reduce((original, settingKey) => {
      const summaryParams = reportSummaries[settingKey]

      summaryParams.isLoading = true

      if (isReportRoute) {
        const { parameters = {} } = summaryParams
        const getSummaryParams = (apiParams, paramName) => {
          apiParams[paramName] = parameters[paramName]()

          return apiParams
        }

        const summaryParameterKeys = Object.keys(parameters)
        const summaryParameters = summaryParameterKeys.reduce(getSummaryParams, {})

        if (!isProgrammaticRoute && isAssociationIdRequiredOnRoute) {
          summaryParams.config = {
            isAlert,
            networkCode,
            publisherId: associationId,
            teamId: subId,
            params: {
              ...currentFilterData,
              ...summaryParameters,
            },
          }
        } else {
          summaryParams.config = {
            isAlert: false,
            params: {
              ...currentFilterData,
              ...summaryParameters,
            },
          }
        }
      }

      original[settingKey] = summaryParams

      return original
    }, reportSummaries)
  },
  async summaries({ commit, dispatch }, summariesConfig) {
    const { isAlert, items, baseReportName } = summariesConfig

    const summaryRequests = await dispatch('getSummaryRequests', summariesConfig)

    commit(actionNames.GET_SUMMARIES_REQUESTED, { baseReportName, summaryRequests })

    return dispatch('getSummaries', { isAlert, items, baseReportName, summaries: summaryRequests })
  },

  /*
   * Column Resizer
   * Updates column width and styles in response to user interactions.
   */

  resetDragging({ commit }) {
    commit('dragging', false)
  },
  startDragging({ commit }, { event, columnIndex }) {
    event.preventDefault()
    event.stopPropagation()

    const pendingColumnIndex = () => columnIndex === -1 && 'pending'
    const startDragging = () => {
      if (!this.dragging) {
        commit('dragging', {
          index: columnIndex,
          start: event.pageX,
          end: null,
        })
      }
    }

    return pendingColumnIndex() || startDragging()
  },
  async keepDragging({ commit, getters, state }, event) {
    const updateSiblings = (dragDistance, dragIndex, frozenColumns, tableColumns) => {
      const columnKey = tableColumns[dragIndex]
      const currentFrozenIndex = frozenColumns.findIndex((col) => col.key === columnKey)
      const isCurrentColumnFrozen = currentFrozenIndex !== -1

      if (isCurrentColumnFrozen) {
        const rightSiblings = frozenColumns.slice(currentFrozenIndex + 1)
        rightSiblings.forEach(async (sibling) => {
          const siblingIndex = tableColumns.findIndex((col) => col.key === sibling.key)
          tableColumns[siblingIndex].left += dragDistance
        })
      }
    }
    const keepDragging = () => {
      const { index: dragIndex, start: dragStart } = state.dragging
      const { baseReportName } = state
      const { frozenColumns, visibleColumns } = getters
      const dragColumn = visibleColumns[dragIndex]

      const startPoint = (typeof dragColumn.pageX === 'number' && dragColumn.pageX) || dragStart
      const currentX = event.pageX

      const dragDistance = currentX - startPoint

      const minWidth = dragColumn.minWidth || 90
      const maxWidth = 900
      const columnWidth = dragDistance + dragColumn.width
      const getMaxWidth = () => columnWidth > maxWidth && maxWidth
      const getMinWidth = () => columnWidth < minWidth && minWidth

      visibleColumns[dragIndex].pageX = currentX
      visibleColumns[dragIndex].width = getMaxWidth() || getMinWidth() || columnWidth

      if (frozenColumns && frozenColumns.length) {
        updateSiblings(dragDistance, dragIndex, frozenColumns, visibleColumns)
      }

      commit('columns', { [baseReportName]: visibleColumns })

      return visibleColumns
    }

    return !state.dragging || keepDragging()
  },
  async stopDragging({ commit, dispatch, state }, event) {
    const { dragging } = state

    if (dragging) {
      dragging.end = event.movementX

      commit('dragging', dragging)

      dispatch('keepDragging', event).then(() => dispatch('resetDragging'))
    }
  },

  showFeatureModal({ commit, rootGetters }) {
    const messages = rootGetters['auth/messages']
    const { 'context-menu': currentMessage } = messages || {}
    const { status: modalStatus, timestamp } = currentMessage || {}
    const isPromptDue = modalStatus === 'defer' && dates.areDatesGte(dates.get(), dates.get(timestamp).add(1, 'hour'), 'hour')

    if (!modalStatus || isPromptDue) {
      const status = 'info'
      const title = 'New Context Menus Replace Tables\' Action Icons'
      const text = ['Right-click columns and rows to access context-relevant actions.', 'Left-click to close context menus.']
      const modalActions = [
        { classes: 'pt-1 coolgray--text', event: 'tools/markAsDefer', isText: true, text: 'Snooze' },
        { classes: 'ml-3 blueblack white--text pa-3', event: 'tools/markAsRead', isText: false, text: 'Got it' },
      ]

      commit('modal', { actions: modalActions, status, text, title })
      commit('modalbar', true)
    }
  },
  async dismissFeatureModal({ commit, dispatch, rootGetters }, newState) {
    commit('modalbar', false)
    // @TODO Resetting the content via `commit('modal', null)` would cause a template error we should discuss in code review.

    const inAliasMode = rootGetters['auth/inAliasMode']
    const messages = rootGetters['auth/messages']

    if (inAliasMode) {
      messages['context-menu'] = {
        status: 'defer',
        timestamp: dates.getUtcDate().format(),
      }
    } else {
      messages['context-menu'] = newState
    }

    const fnSuccess = () => {
      const message = 'Saved Settings'
      const status = 'success'

      // Inform user of success.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status }, { root: true })

      // Log success to analytics.
      const action = newState === 'read' ? 'Read' : 'Deferred'

      fireanalytics.event('goal_completion', {
        category: 'Message Settings',
        description: `${action} Message`,
        label: `${action} Message: Feature Modal`,
      })
    }

    const changes = { messages }

    return dispatch('auth/handleUpdateToUserSettings', { changes, fnSuccess }, { root: true })
  },
  markAsDefer({ dispatch }) {
    dispatch('dismissFeatureModal', { status: 'defer', timestamp: dates.getUtcDate().format() })
  },
  markAsRead({ dispatch }) {
    dispatch('dismissFeatureModal', { status: 'read', timestamp: dates.getUtcDate().format() })
  },
}

export default actions
